// StudentEngagementPage.js - Full-page engagement view for students
import React, { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getMainContentColors } from "../components/MainContentArea";
import { badgeDisplayName } from "../utils/badgeLabel";

// Responsive breakpoints
const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

/**
 * StudentEngagementPage - Full-page engagement screen with sub-tabs:
 * - Tracking: Daily/weekly activity logs
 * - Leaderboard: Class rankings
 * - Badges: Earned achievements
 */
const StudentEngagementPage = ({
  // Tracking data
  engagementLogs = [],
  engagementViewType = "daily",
  onEngagementViewTypeChange,
  engagementHasMore = false,
  onLoadMoreEngagement,
  formatLocalDateShort,
  formatLocalWeekShort,

  // Leaderboard data
  leaderboard = [],
  user_id,
  user_name,
  user_score = 0,
  userRank,

  // Badges data
  badges = [],

  // Theme
  isDarkMode = false,
  t = (s) => s,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const isTablet = width >= TABLET_BREAKPOINT && width < DESKTOP_BREAKPOINT;

  const [activeSubTab, setActiveSubTab] = useState("tracking");

  const colors = getMainContentColors(isDarkMode);

  const subTabs = [
    { id: "tracking", label: t("Tracking"), icon: "chart-timeline-variant" },
    { id: "leaderboard", label: t("Leaderboard"), icon: "trophy-outline" },
    { id: "badges", label: t("Badges"), icon: "medal-outline" },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: isDarkMode ? "#1e293b" : "#fff", borderBottomColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)" }]}>
        <View style={styles.headerContent}>
          <View style={[styles.headerIcon, { backgroundColor: isDarkMode ? "rgba(129, 140, 248, 0.15)" : "rgba(99, 102, 241, 0.1)" }]}>
            <MaterialCommunityIcons
              name="account-group"
              size={32}
              color={isDarkMode ? "#818cf8" : "#6366f1"}
            />
          </View>
          <View style={styles.headerText}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>
              {t("Engagement")}
            </Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {t("Track your activity, rankings, and achievements")}
            </Text>
          </View>
        </View>

        {/* Sub-tabs */}
        <View style={[
          styles.subTabContainer,
          { backgroundColor: isDarkMode ? "#0f172a" : "#f1f5f9" }
        ]}>
          {subTabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.subTab,
                activeSubTab === tab.id && (isDarkMode ? styles.subTabActiveDark : styles.subTabActiveLight),
              ]}
              onPress={() => setActiveSubTab(tab.id)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={18}
                color={activeSubTab === tab.id
                  ? "#fff"
                  : isDarkMode ? "#94a3b8" : "#64748b"}
              />
              <Text style={[
                styles.subTabLabel,
                activeSubTab === tab.id
                  ? styles.subTabLabelActive
                  : (isDarkMode ? styles.subTabLabelDark : styles.subTabLabelLight),
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {/* Tracking Tab */}
        {activeSubTab === "tracking" && (
          <View>
            {/* Daily/Weekly Toggle */}
            <View style={[
              styles.viewTypeToggle,
              { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }
            ]}>
              <TouchableOpacity
                style={[
                  styles.viewTypeBtn,
                  engagementViewType === "daily" && (isDarkMode ? styles.viewTypeBtnActiveDark : styles.viewTypeBtnActiveLight),
                ]}
                onPress={() => onEngagementViewTypeChange("daily")}
              >
                <Text style={[
                  styles.viewTypeBtnText,
                  engagementViewType === "daily"
                    ? styles.viewTypeBtnTextActive
                    : (isDarkMode ? styles.viewTypeBtnTextDark : styles.viewTypeBtnTextLight),
                ]}>
                  {t("Daily")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.viewTypeBtn,
                  engagementViewType === "weekly" && (isDarkMode ? styles.viewTypeBtnActiveDark : styles.viewTypeBtnActiveLight),
                ]}
                onPress={() => onEngagementViewTypeChange("weekly")}
              >
                <Text style={[
                  styles.viewTypeBtnText,
                  engagementViewType === "weekly"
                    ? styles.viewTypeBtnTextActive
                    : (isDarkMode ? styles.viewTypeBtnTextDark : styles.viewTypeBtnTextLight),
                ]}>
                  {t("Weekly")}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Engagement Logs */}
            {engagementLogs.length > 0 ? (
              <View style={[
                styles.logsGrid,
                { flexDirection: isDesktop ? "row" : "column", flexWrap: isDesktop ? "wrap" : "nowrap" }
              ]}>
                {engagementLogs
                  .slice()
                  .sort((a, b) => new Date(b.last_active) - new Date(a.last_active))
                  .map((log, index) => (
                    <View
                      key={index}
                      style={[
                        styles.logCard,
                        { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
                        isDesktop && { width: "48%" }
                      ]}
                    >
                      <Text style={[styles.logDate, { color: colors.textPrimary }]}>
                        {engagementViewType === "weekly"
                          ? formatLocalWeekShort(log.period || log.last_active)
                          : formatLocalDateShort(log.period || log.day || log.last_active)}
                      </Text>
                      <View style={styles.logStatsRow}>
                        <View style={styles.logStatItem}>
                          <Text style={[styles.logStatValue, { color: isDarkMode ? "#60a5fa" : "#3b82f6" }]}>
                            {log.clicks}
                          </Text>
                          <Text style={[styles.logStatLabel, { color: colors.textSecondary }]}>
                            {t("Touch Points")}
                          </Text>
                        </View>
                        <View style={styles.logStatItem}>
                          <Text style={[styles.logStatValue, { color: isDarkMode ? "#a78bfa" : "#8b5cf6" }]}>
                            {log.quiz_attempts}
                          </Text>
                          <Text style={[styles.logStatLabel, { color: colors.textSecondary }]}>
                            {t("Quiz Attempts")}
                          </Text>
                        </View>
                        <View style={styles.logStatItem}>
                          <Text style={[
                            styles.logStatValue,
                            { color: isDarkMode ? "#34d399" : "#10b981" }
                          ]}>
                            {log.accuracy != null ? log.accuracy.toFixed(1) + "%" : "N/A"}
                          </Text>
                          <Text style={[styles.logStatLabel, { color: colors.textSecondary }]}>
                            {t("Accuracy")}
                          </Text>
                        </View>
                      </View>
                    </View>
                  ))}
              </View>
            ) : (
              <View style={[styles.emptyState, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
                <MaterialCommunityIcons
                  name="chart-timeline-variant"
                  size={48}
                  color={isDarkMode ? "#475569" : "#cbd5e1"}
                />
                <Text style={[styles.emptyStateTitle, { color: colors.textPrimary }]}>
                  {t("No activity yet")}
                </Text>
                <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                  {t("Start learning to see your engagement data here.")}
                </Text>
              </View>
            )}

            {/* Load More Button */}
            {engagementHasMore && (
              <TouchableOpacity
                style={[styles.loadMoreBtn, { backgroundColor: isDarkMode ? "#334155" : "#e2e8f0" }]}
                onPress={onLoadMoreEngagement}
              >
                <Text style={[styles.loadMoreText, { color: isDarkMode ? "#94a3b8" : "#64748b" }]}>
                  {t("Load More")}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Leaderboard Tab */}
        {activeSubTab === "leaderboard" && (
          <View>
            {leaderboard.length > 0 ? (
              <View style={[styles.leaderboardCard, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
                {leaderboard.slice(0, 10).map((entry, index) => (
                  <View
                    key={index}
                    style={[
                      styles.leaderboardItem,
                      index < leaderboard.slice(0, 10).length - 1 && {
                        borderBottomWidth: 1,
                        borderBottomColor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                      },
                      entry.email === user_id && {
                        backgroundColor: isDarkMode ? "rgba(99, 102, 241, 0.15)" : "rgba(99, 102, 241, 0.08)",
                      },
                    ]}
                  >
                    <View style={[
                      styles.rankBadge,
                      index === 0 && styles.rankBadgeGold,
                      index === 1 && styles.rankBadgeSilver,
                      index === 2 && styles.rankBadgeBronze,
                    ]}>
                      <Text style={[
                        styles.rankText,
                        (index < 3) && { color: "#fff" }
                      ]}>
                        {index + 1}
                      </Text>
                    </View>
                    <Text style={[styles.leaderboardName, { color: colors.textPrimary }]}>
                      {entry.email === user_id ? entry.name : (entry.name ? entry.name.charAt(0) + "***" : "Student")}
                    </Text>
                    <Text style={[styles.leaderboardScore, { color: isDarkMode ? "#34d399" : "#10b981" }]}>
                      {entry.total_score.toFixed(1)} pts
                    </Text>
                  </View>
                ))}

                {/* Current User (If Outside Top 10) */}
                {leaderboard.length > 10 && userRank > 10 && (
                  <>
                    <View style={styles.leaderboardDivider}>
                      <Text style={{ color: colors.textSecondary }}>•••</Text>
                    </View>
                    <View style={[
                      styles.leaderboardItem,
                      { backgroundColor: isDarkMode ? "rgba(99, 102, 241, 0.15)" : "rgba(99, 102, 241, 0.08)" }
                    ]}>
                      <View style={styles.rankBadge}>
                        <Text style={styles.rankText}>{userRank}</Text>
                      </View>
                      <Text style={[styles.leaderboardName, { color: colors.textPrimary }]}>
                        {user_name} {t("(You)")}
                      </Text>
                      <Text style={[styles.leaderboardScore, { color: isDarkMode ? "#34d399" : "#10b981" }]}>
                        {user_score?.toFixed(1)} pts
                      </Text>
                    </View>
                  </>
                )}
              </View>
            ) : (
              <View style={[styles.emptyState, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
                <MaterialCommunityIcons
                  name="trophy-outline"
                  size={48}
                  color={isDarkMode ? "#475569" : "#cbd5e1"}
                />
                <Text style={[styles.emptyStateTitle, { color: colors.textPrimary }]}>
                  {t("No leaderboard data")}
                </Text>
                <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                  {t("Complete activities to appear on the leaderboard.")}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Badges Tab */}
        {activeSubTab === "badges" && (
          <View>
            {badges.length > 0 ? (
              <View style={[
                styles.badgesGrid,
                { flexDirection: isDesktop ? "row" : "column", flexWrap: "wrap" }
              ]}>
                {badges.map((badge, index) =>
                  badge.name ? (
                    <View
                      key={index}
                      style={[
                        styles.badgeCard,
                        { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
                        isDesktop && { width: "30%" }
                      ]}
                    >
                      <View style={[
                        styles.badgeIcon,
                        { backgroundColor: isDarkMode ? "rgba(251, 191, 36, 0.2)" : "rgba(245, 158, 11, 0.1)" }
                      ]}>
                        <MaterialCommunityIcons
                          name="medal"
                          size={32}
                          color={isDarkMode ? "#fbbf24" : "#f59e0b"}
                        />
                      </View>
                      <Text style={[styles.badgeName, { color: colors.textPrimary }]}>
                        {badgeDisplayName(badge, t)}
                      </Text>
                      {badge.description && (
                        <Text style={[styles.badgeDescription, { color: colors.textSecondary }]}>
                          {badge.description}
                        </Text>
                      )}
                    </View>
                  ) : null
                )}
              </View>
            ) : (
              <View style={[styles.emptyState, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
                <MaterialCommunityIcons
                  name="medal-outline"
                  size={48}
                  color={isDarkMode ? "#475569" : "#cbd5e1"}
                />
                <Text style={[styles.emptyStateTitle, { color: colors.textPrimary }]}>
                  {t("No badges yet")}
                </Text>
                <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                  {t("Keep learning to earn badges and achievements!")}
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.05)", // overridden inline for dark mode
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    gap: 16,
  },
  headerIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "rgba(99, 102, 241, 0.1)",
    justifyContent: "center",
    alignItems: "center",
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
  },
  subTabContainer: {
    flexDirection: "row",
    borderRadius: 12,
    padding: 4,
  },
  subTab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  subTabActiveLight: {
    backgroundColor: "#6366f1",
  },
  subTabActiveDark: {
    backgroundColor: "#6366f1",
  },
  subTabLabel: {
    fontSize: 13,
    fontWeight: "600",
  },
  subTabLabelLight: {
    color: "#64748b",
  },
  subTabLabelDark: {
    color: "#94a3b8",
  },
  subTabLabelActive: {
    color: "#fff",
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 100,
  },
  viewTypeToggle: {
    flexDirection: "row",
    borderRadius: 12,
    padding: 4,
    marginBottom: 20,
  },
  viewTypeBtn: {
    flex: 1,
    paddingVertical: 10,
    alignItems: "center",
    borderRadius: 8,
  },
  viewTypeBtnActiveLight: {
    backgroundColor: "#10b981",
  },
  viewTypeBtnActiveDark: {
    backgroundColor: "#10b981",
  },
  viewTypeBtnText: {
    fontSize: 13,
    fontWeight: "600",
  },
  viewTypeBtnTextLight: {
    color: "#64748b",
  },
  viewTypeBtnTextDark: {
    color: "#94a3b8",
  },
  viewTypeBtnTextActive: {
    color: "#fff",
  },
  logsGrid: {
    gap: 12,
  },
  logCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  logDate: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
  },
  logStatsRow: {
    flexDirection: "row",
    justifyContent: "space-around",
  },
  logStatItem: {
    alignItems: "center",
  },
  logStatValue: {
    fontSize: 24,
    fontWeight: "700",
  },
  logStatLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  loadMoreBtn: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: "600",
  },
  leaderboardCard: {
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  leaderboardItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    gap: 12,
  },
  leaderboardDivider: {
    alignItems: "center",
    paddingVertical: 8,
  },
  rankBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(148, 163, 184, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  rankBadgeGold: {
    backgroundColor: "#fbbf24",
  },
  rankBadgeSilver: {
    backgroundColor: "#94a3b8",
  },
  rankBadgeBronze: {
    backgroundColor: "#cd7f32",
  },
  rankText: {
    fontSize: 14,
    fontWeight: "700",
  },
  leaderboardName: {
    flex: 1,
    fontSize: 15,
    fontWeight: "500",
  },
  leaderboardScore: {
    fontSize: 15,
    fontWeight: "700",
  },
  badgesGrid: {
    gap: 16,
  },
  badgeCard: {
    padding: 20,
    borderRadius: 16,
    alignItems: "center",
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  badgeIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
  },
  badgeName: {
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  badgeDescription: {
    fontSize: 13,
    textAlign: "center",
    marginTop: 4,
  },
  emptyState: {
    padding: 40,
    borderRadius: 16,
    alignItems: "center",
    gap: 12,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: "center",
    maxWidth: 280,
  },
});

export default StudentEngagementPage;
